import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Toolbar,
  Sort,
  Edit,
  Inject,
  Filter,
} from '@syncfusion/ej2-react-grids';
import { Query } from '@syncfusion/ej2-data';
import { message, Button, Modal, Input } from 'antd';
import { useTranslation } from '../../i18n';

import withMosadIsolation from '../../utils/withMosadIsolation';
import { fetchMosadEmployees, inviteEmployees, updateEmployee } from '../../redux/slices/employeesSlice';

const ROLE_VALUES = ['owner', 'admin', 'employee', 'viewer'];

function parseInviteEmailList(raw) {
  if (!raw || typeof raw !== 'string') return [];
  const parts = raw.split(/[\n,;]+/);
  return [...new Set(parts.map((s) => s.trim()).filter(Boolean))];
}

const Employees = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const statusOptions = useMemo(
    () => [
      { value: 'U', text: t('ui.mosads.workerUnverified') },
      { value: 'A', text: t('ui.mosads.active') },
      { value: 'P', text: t('ui.mosads.workerPostponed') },
      { value: 'I', text: t('ui.mosads.workerInvited') },
    ],
    [t]
  );

  const roleOptions = useMemo(
    () =>
      ROLE_VALUES.map((role) => ({
        value: role,
        text: t(`ui.mosads.${role}`),
      })),
    [t]
  );
  const { employeesList } = useSelector((state) => state.employees);
  const { mosadData } = useSelector((state) => state.user);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmailsText, setInviteEmailsText] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  const canManageEmployees = ['owner', 'admin'].includes(mosadData?.role);

  const filterSettings = { type: 'Excel' };
  const toolbarOptions = canManageEmployees ? ['Add', 'Edit', 'Update', 'Cancel'] : [];
  const editSettings = {
    allowEditing: canManageEmployees,
    allowAdding: canManageEmployees,
    allowDeleting: false,
    mode: 'Normal',
  };
  const pageSettings = { pageCount: 5 };
  const gridRef = React.useRef(null);

  useEffect(() => {
    dispatch(fetchMosadEmployees());
  }, [dispatch]);

  function formatDateDdMmYyyy(value) {
    if (value === null || value === undefined || value === '') return '';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function statusValueAccessor(field, data) {
    const code = data?.[field];
    if (code === 'U') return t('ui.mosads.workerUnverified');
    if (code === 'A') return t('ui.mosads.active');
    if (code === 'P') return t('ui.mosads.workerPostponed');
    if (code === 'I') return t('ui.mosads.workerInvited');
    return code || '';
  }

  function roleValueAccessor(field, data) {
    const role = data?.[field];
    if (!role) return '';
    if (ROLE_VALUES.includes(role)) return t(`ui.mosads.${role}`);
    return t('ui.mosads.unknown');
  }

  function extractErrorMessage(err) {
    if (!err) return t('notifications.frontend.common.unknownError');
    if (typeof err === 'string') return err;
    if (Array.isArray(err?.message)) {
      return err.message
        .map((e) => (typeof e === 'string' ? e : e?.msg || e?.message || String(e)))
        .join(', ');
    }
    if (Array.isArray(err)) {
      return err.map((e) => (typeof e === 'string' ? e : e?.msg || e?.message || String(e))).join(', ');
    }
    if (typeof err?.message === 'string') return err.message;
    if (typeof err?.error === 'string') return err.error;
    if (Array.isArray(err?.error)) {
      return err.error
        .map((e) => (typeof e === 'string' ? e : e?.msg || e?.message || String(e)))
        .join(', ');
    }
    if (typeof err?.msg === 'string') return err.msg;
    return t('notifications.frontend.common.unknownError');
  }

  async function actionBegin(args) {
    if (args.requestType !== 'save') return;

    if (args.action === 'add') {
      args.cancel = true;
      const raw = args.data?.usr_email;
      const mails =
        typeof raw === 'string'
          ? parseInviteEmailList(raw).map((m) => m.toLowerCase())
          : [];
      if (mails.length === 0) {
        message.error(t('ui.employees.inviteEmailsRequired'));
        return;
      }
      try {
        const result = await dispatch(inviteEmployees(mails)).unwrap();
        const serverMsg = typeof result?.message === 'string' ? result.message : '';
        if (result?.success === false) {
          message.warning(serverMsg || t('notifications.frontend.common.unknownError'), 5);
          if (Array.isArray(result?.notAddedEmployees) && result.notAddedEmployees.length > 0) {
            message.error(String(result.notAddedEmployees.join(', ')), 5);
          }
        } else {
          message.success(serverMsg || t('notifications.frontend.common.employeesInviteSuccess'));
          gridRef.current?.closeEdit();
        }
        await dispatch(fetchMosadEmployees());
      } catch (err) {
        const errMsg = extractErrorMessage(err);
        message.error(errMsg, 3);
      }
      return;
    }

    if (args.action !== 'edit') return;
    try {
      const usr_id = args.data?.usr_id;
      if (!usr_id) {
        args.cancel = true;
        message.error(t('notifications.frontend.common.employeeIdMissing'));
        return;
      }

      await dispatch(
        updateEmployee({
          usr_id,
          firstName: args.data?.usr_firstName,
          lastName: args.data?.usr_lastName,
          role: args.data?.role,
          status: args.data?.usr_status,
        })
      ).unwrap();
      message.success(t('notifications.frontend.common.employeeUpdatedSuccessfully'));
      await dispatch(fetchMosadEmployees());
    } catch (err) {
      args.cancel = true;
      const errMsg = extractErrorMessage(err);
      message.error(errMsg, 3);
    }
  }

  async function handleInviteEmployeesSubmit() {
    const mails = parseInviteEmailList(inviteEmailsText);
    if (mails.length === 0) {
      message.error(t('ui.employees.inviteEmailsRequired'));
      return;
    }
    setInviteSubmitting(true);
    try {
      const result = await dispatch(inviteEmployees(mails)).unwrap();
      const serverMsg = typeof result?.message === 'string' ? result.message : '';
      if (result?.success === false) {
        message.warning(serverMsg || t('notifications.frontend.common.unknownError'), 5);
        if (Array.isArray(result?.notAddedEmployees) && result.notAddedEmployees.length > 0) {
          message.error(String(result.notAddedEmployees.join(', ')), 5);
        }
      } else {
        message.success(serverMsg || t('notifications.frontend.common.employeesInviteSuccess'));
        setInviteModalOpen(false);
        setInviteEmailsText('');
      }
      await dispatch(fetchMosadEmployees());
    } catch (err) {
      message.error(extractErrorMessage(err), 3);
    } finally {
      setInviteSubmitting(false);
    }
  }

  return (
    <div className="control-pane">
      <div className="control-section">
        <h2>{t('ui.employees.title')}</h2>
          {canManageEmployees ? (
            <Button style={{ marginBottom: 12 }} type="primary" onClick={() => setInviteModalOpen(true)}>
              {t('ui.employees.inviteEmployees')}
            </Button>
          ) : null}
        {/* <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <h2 style={{ margin: 0 }}>{t('ui.employees.title')}</h2>
          {canManageEmployees ? (
            <Button type="primary" onClick={() => setInviteModalOpen(true)}>
              {t('ui.employees.inviteEmployees')}
            </Button>
          ) : null}
        </div> */}
        <Modal
          title={t('ui.employees.inviteModalTitle')}
          open={inviteModalOpen}
          onCancel={() => !inviteSubmitting && setInviteModalOpen(false)}
          onOk={handleInviteEmployeesSubmit}
          okText={t('ui.employees.inviteSubmit')}
          confirmLoading={inviteSubmitting}
          destroyOnClose
          afterClose={() => setInviteEmailsText('')}
        >
          <div style={{ marginBottom: 8 }}>{t('ui.employees.inviteEmailsLabel')}</div>
          <Input.TextArea
            value={inviteEmailsText}
            onChange={(e) => setInviteEmailsText(e.target.value)}
            placeholder={t('ui.employees.inviteEmailsPlaceholder')}
            rows={6}
            disabled={inviteSubmitting}
          />
        </Modal>
        <div style={{ width: '100%', overflowX: 'auto' }}>
        <GridComponent
          ref={gridRef}
          dataSource={(employeesList || []).map((item) => ({ ...item }))}
          width="100%"
          toolbar={toolbarOptions}
          allowSorting
          allowFiltering
          filterSettings={filterSettings}
          allowPaging
          editSettings={editSettings}
          pageSettings={pageSettings}
          enableRtl
          actionBegin={actionBegin}
        >
          <ColumnsDirective>
            <ColumnDirective
              field="usr_id"
              headerText={t('ui.employees.userId')}
              width="1"
              visible={false}
              isPrimaryKey
            />
            <ColumnDirective
              field="usr_email"
              headerText={t('ui.employees.email')}
              width="200"
              textAlign="Center"
              allowEditing={canManageEmployees}
            />
            <ColumnDirective
              field="usr_firstName"
              headerText={t('ui.employees.firstName')}
              width="120"
              textAlign="Center"
            />
            <ColumnDirective
              field="usr_lastName"
              headerText={t('ui.employees.lastName')}
              width="120"
              textAlign="Center"
            />
            <ColumnDirective
              field="role"
              headerText={t('ui.employees.role')}
              width="100"
              textAlign="Center"
              valueAccessor={roleValueAccessor}
              editType="dropdownedit"
              edit={{
                params: {
                  dataSource: roleOptions,
                  fields: { text: 'text', value: 'value' },
                  query: new Query(),
                },
              }}
            />
            <ColumnDirective
              field="usr_status"
              headerText={t('ui.employees.accountStatus')}
              width="120"
              textAlign="Center"
              valueAccessor={statusValueAccessor}
              editType="dropdownedit"
              edit={{
                params: {
                  dataSource: statusOptions,
                  fields: { text: 'text', value: 'value' },
                  query: new Query(),
                },
              }}
            />
            <ColumnDirective
              field="usr_last_login"
              headerText={t('ui.employees.lastLogin')}
              width="120"
              textAlign="Center"
              valueAccessor={(f, data) => formatDateDdMmYyyy(data?.[f])}
              allowEditing={false}
            />
          </ColumnsDirective>
          <Inject services={[Page, Toolbar, Edit, Sort, Filter]} />
        </GridComponent>
        </div>
      </div>
    </div>
  );
};

export default withMosadIsolation(Employees);
